// Pure-ish image helper: no Firebase, no React. Kept separate from the storage
// adapter so the sizing policy can change without touching upload code.

export const MAX_PHOTO_EDGE = 512;
export const PHOTO_QUALITY = 0.85;
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export const ACCEPTED_PHOTO_TYPES = Object.freeze([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
]);

/** Why this file cannot be used, or null when it is fine. */
export function photoRejectionReason(file) {
  if (!file) return "Choose an image to upload.";
  if (!String(file.type || "").startsWith("image/")) {
    return "That file is not an image.";
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return "That image is larger than 8MB. Try a smaller one.";
  }
  return null;
}

/** Longest-edge-capped dimensions that preserve aspect ratio. */
export function fittedSize(width, height, maxEdge = MAX_PHOTO_EDGE) {
  if (!width || !height) return { width: 0, height: 0 };
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That image could not be read."));
    };
    image.src = url;
  });
}

/**
 * Downscale to a square-bounded JPEG before upload. Connection photos are only
 * ever shown at avatar and hero size, so shipping the original is wasteful and
 * slow on a phone connection.
 */
export async function resizeImageFile(file, maxEdge = MAX_PHOTO_EDGE) {
  const image = await loadImage(file);
  const { width, height } = fittedSize(image.naturalWidth, image.naturalHeight, maxEdge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", PHOTO_QUALITY)
  );
  // A browser that refuses to encode is not worth failing the upload over.
  return blob || file;
}
